import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowLeft
} from 'lucide-react';
import { getContractors, SEED_CONTRACTORS } from '../services/contractors';
import { useAuth } from '../contexts/AuthContext';
import { Contractor } from '../types';
import PipelineBoard from './PipelineBoard';

export default function SavedContracts() {
    const { isSuperAdmin, appUser, isContractorAdmin } = useAuth();
    const [contractors, setContractors] = useState<Contractor[]>([]);
    const [selectedContractor, setSelectedContractor] = useState<string>('');

    useEffect(() => {
        loadContractors();
    }, []);

    const loadContractors = async () => {
        try {
            const data = await getContractors();
            // If super admin, show all. If not, only show user's contractor.
            let list = data.length > 0 ? data : SEED_CONTRACTORS;

            if (!isSuperAdmin && appUser?.contractorId) {
                list = list.filter(c => c.id === appUser.contractorId);
            }

            setContractors(list);

            // Set initial selection
            if (list.length > 0) {
                // If user has a contractorId, prefer that. Otherwise default to first.
                const defaultId = (!isSuperAdmin && appUser?.contractorId)
                    ? appUser.contractorId
                    : list[0].id;
                setSelectedContractor(defaultId);
            }
        } catch (err) {
            console.error('Error loading contractors:', err);
            setContractors(SEED_CONTRACTORS);
            if (SEED_CONTRACTORS.length > 0) {
                setSelectedContractor(SEED_CONTRACTORS[0].id);
            }
        }
    };

    return (
        <div className="space-y-6 fade-in max-w-[1800px] mx-auto pb-4 h-[calc(100vh-80px)] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4">
                <div className="flex items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white tracking-tight">Pipeline</h1>
                        <p className="text-gray-400 text-sm mt-1">
                            Manage capture workflow
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {/* Contractor Selector (Admin) */}
                    {contractors.length > 1 && (
                        <div className="flex bg-black/40 p-1 rounded-lg border border-white/10">
                            <select
                                value={selectedContractor}
                                onChange={(e) => setSelectedContractor(e.target.value)}
                                className="bg-transparent text-sm border-none focus:ring-0 text-gray-300"
                                disabled={!isSuperAdmin && !isContractorAdmin}
                            >
                                {contractors.map(c => <option key={c.id} value={c.id} className="bg-gray-900">{c.displayName}</option>)}
                            </select>
                        </div>
                    )}

                    <Link to="/" className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium text-sm transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Find Opportunities
                    </Link>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0">
                <PipelineBoard contractorId={selectedContractor} />
            </div>
        </div>
    );
}
